/**
 * Admin — Unit price override page.
 *
 * CRUD management of price overrides for EP/OTC trading pairs.
 * Operators can create/edit overrides; finance & viewers are read-only.
 */
import { useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Tag,
  Select,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { DataTable } from '../../components/DataTable';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { useQueryClient } from '@tanstack/react-query';

interface UnitPriceOverride {
  id: number;
  pair: string;
  override_price: string;
  is_active: boolean;
  reason: string | null;
  set_by: number;
  create_time: string;
  update_time: string;
}

export function UnitPricePage() {
  const hasNode = useAuthStore((s) => s.hasNode);
  const queryClient = useQueryClient();

  const [pairFilter, setPairFilter] = useState<string | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<string | undefined>(undefined);

  // Create / Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UnitPriceOverride | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalOpen(true);
  };

  const openEdit = (row: UnitPriceOverride) => {
    setEditing(row);
    form.setFieldsValue({
      pair: row.pair,
      override_price: row.override_price,
      is_active: row.is_active,
      reason: row.reason ?? '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editing) {
        await api.patch(`/api/admin/unit-price/${editing.id}`, {
          override_price: values.override_price,
          is_active: values.is_active,
          reason: values.reason || undefined,
        });
        message.success('已更新');
      } else {
        await api.post('/api/admin/unit-price', {
          pair: values.pair,
          override_price: values.override_price,
          is_active: values.is_active,
          reason: values.reason || undefined,
        });
        message.success('已创建');
      }
      setModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['unit-price-list'] });
    } catch (err: any) {
      if (err?.errorFields) return; // form validation
      message.error(err?.message ?? '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '交易对', dataIndex: 'pair', key: 'pair', width: 120 },
    {
      title: '覆盖价格',
      dataIndex: 'override_price',
      key: 'override_price',
      width: 140,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (v: boolean) =>
        v ? <Tag color="green">生效</Tag> : <Tag color="default">停用</Tag>,
    },
    { title: '原因', dataIndex: 'reason', key: 'reason', width: 200, ellipsis: true,
      render: (v: string | null) => v ?? '-' },
    { title: '设置人', dataIndex: 'set_by', key: 'set_by', width: 80 },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '更新时间',
      dataIndex: 'update_time',
      key: 'update_time',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
    ...(hasNode('unit_price.update')
      ? [
          {
            title: '操作',
            key: 'actions',
            width: 80,
            render: (_: unknown, row: UnitPriceOverride) => (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
              >
                编辑
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 160 }}
          placeholder="筛选交易对"
          value={pairFilter ?? ''}
          onChange={(e) => setPairFilter(e.target.value || undefined)}
          allowClear
        />
        <Select
          style={{ width: 120 }}
          placeholder="状态"
          allowClear
          value={activeFilter}
          onChange={(v) => setActiveFilter(v)}
          options={[
            { value: 'true', label: '生效' },
            { value: 'false', label: '停用' },
          ]}
        />
        {hasNode('unit_price.create') && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建覆盖
          </Button>
        )}
      </Space>

      <DataTable<UnitPriceOverride>
        queryKey={['unit-price-list']}
        endpoint="/api/admin/unit-price"
        extraParams={{
          ...(pairFilter ? { pair: pairFilter } : {}),
          ...(activeFilter !== undefined ? { is_active: activeFilter } : {}),
        }}
        columns={columns}
        rowKey="id"
      />

      <Modal
        title={editing ? `编辑覆盖 #${editing.id}` : '新建单价覆盖'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="pair"
            label="交易对"
            rules={[{ required: true, message: '请输入交易对' }]}
          >
            <Input placeholder="例如 USDT/CNY" disabled={!!editing} maxLength={32} />
          </Form.Item>
          <Form.Item
            name="override_price"
            label="覆盖价格"
            rules={[
              { required: true, message: '请输入价格' },
              {
                validator: (_, v) => {
                  if (!v) return Promise.resolve();
                  const n = Number(v);
                  if (isNaN(n) || n <= 0) return Promise.reject('价格必须为正数');
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="例如 7.2500" maxLength={30} />
          </Form.Item>
          <Form.Item name="is_active" label="是否生效" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <Input.TextArea rows={2} maxLength={500} placeholder="可选，备注原因" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UnitPricePage;
